'use client';

import React, { useState } from 'react';
import { Plus, Search, Edit2, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { MOCK_FEED_TYPES, FeedType, FeedCategory } from './feedData';

const CATEGORY_COLORS: Record<FeedCategory, string> = {
  Starter: 'bg-yellow-100 text-yellow-700',
  Grower: 'bg-green-100 text-green-700',
  Finisher: 'bg-blue-100 text-blue-700',
  Layer: 'bg-purple-100 text-purple-700',
  Breeder: 'bg-pink-100 text-pink-700',
  Supplement: 'bg-orange-100 text-orange-700',
};

export default function FeedTypesTab() {
  const [feedTypes, setFeedTypes] = useState<FeedType[]>(MOCK_FEED_TYPES);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<FeedType | null>(null);
  const [form, setForm] = useState({ name: '', code: '', category: 'Starter' as FeedCategory, manufacturer: '', description: '', proteinContent: '', energyContent: '', unitCost: '', targetBirdType: '', ageRangeWeeks: '' });

  const filtered = feedTypes.filter(f => {
    const q = search.toLowerCase();
    const matchSearch = !q || f.name.toLowerCase().includes(q) || f.code.toLowerCase().includes(q) || f.manufacturer.toLowerCase().includes(q);
    const matchCat = filterCategory === 'all' || f.category === filterCategory;
    return matchSearch && matchCat;
  });

  function openAdd() {
    setEditItem(null);
    setForm({ name: '', code: '', category: 'Starter', manufacturer: '', description: '', proteinContent: '', energyContent: '', unitCost: '', targetBirdType: '', ageRangeWeeks: '' });
    setShowModal(true);
  }

  function openEdit(ft: FeedType) {
    setEditItem(ft);
    setForm({ name: ft.name, code: ft.code, category: ft.category, manufacturer: ft.manufacturer, description: ft.description, proteinContent: String(ft.proteinContent), energyContent: String(ft.energyContent), unitCost: String(ft.unitCost), targetBirdType: ft.targetBirdType, ageRangeWeeks: ft.ageRangeWeeks });
    setShowModal(true);
  }

  function handleSave() {
    if (!form.name || !form.code) return;
    if (editItem) {
      setFeedTypes(prev => prev.map(f => f.id === editItem.id ? { ...f, ...form, proteinContent: Number(form.proteinContent), energyContent: Number(form.energyContent), unitCost: Number(form.unitCost) } : f));
    } else {
      const newFt: FeedType = { id: `ft-${Date.now()}`, ...form, proteinContent: Number(form.proteinContent), energyContent: Number(form.energyContent), unitCost: Number(form.unitCost), unit: 'kg', isActive: true, createdAt: '2026-09-05' };
      setFeedTypes(prev => [...prev, newFt]);
    }
    setShowModal(false);
  }

  function toggleActive(id: string) {
    setFeedTypes(prev => prev.map(f => f.id === id ? { ...f, isActive: !f.isActive } : f));
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search feed types…" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" style={{ backgroundColor: 'var(--card-bg)' }} />
        </div>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--card-bg)' }}>
          <option value="all">All Categories</option>
          {(['Starter','Grower','Finisher','Layer','Breeder','Supplement'] as FeedCategory[]).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: 'var(--primary)' }}>
          <Plus size={14} /> Add Feed Type
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100" style={{ backgroundColor: 'var(--surface)' }}>
              {['Code','Name','Category','Manufacturer','Protein %','Energy kcal/kg','Cost/kg (UGX)','Target Bird','Age Range','Status','Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((ft, i) => (
              <tr key={ft.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : ''}`} style={{ backgroundColor: 'var(--card-bg)' }}>
                <td className="px-4 py-3 font-mono text-xs font-semibold text-indigo-600">{ft.code}</td>
                <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{ft.name}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[ft.category]}`}>{ft.category}</span></td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{ft.manufacturer}</td>
                <td className="px-4 py-3 text-center font-medium">{ft.proteinContent > 0 ? `${ft.proteinContent}%` : '—'}</td>
                <td className="px-4 py-3 text-center">{ft.energyContent > 0 ? ft.energyContent.toLocaleString() : '—'}</td>
                <td className="px-4 py-3 text-right font-medium">{ft.unitCost.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-600">{ft.targetBirdType}</td>
                <td className="px-4 py-3 text-gray-600">{ft.ageRangeWeeks} wks</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ft.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{ft.isActive ? 'Active' : 'Inactive'}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(ft)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 transition-colors"><Edit2 size={13} /></button>
                    <button onClick={() => toggleActive(ft.id)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">{ft.isActive ? <ToggleRight size={15} className="text-emerald-500" /> : <ToggleLeft size={15} />}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="py-12 text-center text-gray-400 text-sm">No feed types found</div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="w-full max-w-lg rounded-2xl shadow-2xl p-6" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-800">{editItem ? 'Edit Feed Type' : 'Add Feed Type'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Feed Name', key: 'name', type: 'text', full: true },
                { label: 'Code', key: 'code', type: 'text' },
                { label: 'Manufacturer', key: 'manufacturer', type: 'text' },
                { label: 'Protein Content (%)', key: 'proteinContent', type: 'number' },
                { label: 'Energy (kcal/kg)', key: 'energyContent', type: 'number' },
                { label: 'Cost per kg (UGX)', key: 'unitCost', type: 'number' },
                { label: 'Target Bird Type', key: 'targetBirdType', type: 'text' },
                { label: 'Age Range (weeks)', key: 'ageRangeWeeks', type: 'text' },
              ].map(f => (
                <div key={f.key} className={f.full ? 'col-span-2' : ''}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                  <input type={f.type} value={(form as any)[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" style={{ backgroundColor: 'var(--surface)' }} />
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <select value={form.category} onChange={e => setForm(prev => ({ ...prev, category: e.target.value as FeedCategory }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--surface)' }}>
                  {(['Starter','Grower','Finisher','Layer','Breeder','Supplement'] as FeedCategory[]).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none" style={{ backgroundColor: 'var(--surface)' }} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: 'var(--primary)' }}>{editItem ? 'Save Changes' : 'Add Feed Type'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
